import { useCallback, useContext, useEffect, useState } from 'react';

import type { GridSortModel } from '@mui/x-data-grid';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';

import { useBooleanState } from '@vyce/core/src/hooks';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { useInfiniteScroll } from '@vyce/core/src/hooks/useInfiniteScroll';
import { useMainDashboardFilter } from '@vyce/core/src/hooks/useMainDashboardFilter';
import { formatTimeSortModel } from '@vyce/core/src/utils/sorting';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useWidgetErrorHandler } from '@vyce/core/src/hooks/useWidgetErrorHandler';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { DormantWorkerDTO } from '@vyce/core/src/api/types';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';

import { Props } from '../types';
import { DORMANT_PERIOD_WEEKS } from '../constants';

const defaultSortModel: GridSortModel = [{ field: 'last_clock_in_date', sort: 'asc' }];

export const useWidgetData = ({
  selectedCompanyId,
  mainDashboardFilters,
  fetchDormantWorkers,
  removeDormantWorker,
  onForbidden,
}: Props) => {
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] = useBooleanState(false);
  const [isRemoving, setRemovingTrue, setRemovingFalse] = useBooleanState(false);
  const [workerToRemove, setWorkerToRemove] = useState<DormantWorkerDTO | null>(null);

  const { handleServerError } = useContext(NotificationContext);

  // Loading the widget: a 403 hides it instead of raising a toast.

  const handleWidgetError = useWidgetErrorHandler(onForbidden);
  const { isMobile } = useContext(DeviceContext);

  const { sortModel, total, substring, setTotal, handleSortModelChange, handleSearchChange } = useTable({
    defaultSortModel,
  });

  const {
    isLoadingList,
    data,
    setNewData,
    setInitialLoadingStatusesFalse,
    setInitialLoadingStatusesTrue,
    resetLoadingList,
  } = useInfiniteScroll<DormantWorkerDTO>([]);

  const { selectedLocationOptions, unselectedLocationOptions, getLocationsForRequest } =
    useMainDashboardFilter({
      mainDashboardFilters,
    });

  const getDormantWorkers = async (offset: number = 0, signal?: AbortSignal) => {
    if (!selectedCompanyId) {
      return;
    }

    const itemStatusMap: Record<number, boolean> = setInitialLoadingStatusesFalse(offset, GRID_PAGE_SIZE);
    setLoadingTrue();
    try {
      const { data } = await fetchDormantWorkers({
        companyId: selectedCompanyId,
        payload: {
          offset,
          limit: GRID_PAGE_SIZE,
          order_by: formatTimeSortModel<DormantWorkerDTO>(sortModel),
          worker_substring: substring,
          site_ids: getLocationsForRequest(selectedLocationOptions.length ? 'selected' : 'all'),
          from_date: dayjs().subtract(DORMANT_PERIOD_WEEKS, 'weeks').format('YYYY-MM-DD'),
        },
        signal,
      });
      setTotal(data.count);
      setNewData({ newData: data.items, offset });

      setInitialLoadingStatusesTrue({ offset, pageSize: GRID_PAGE_SIZE, itemStatusMap });
      setLoadingFalse();
    } catch (e) {
      const err = e as AxiosError;
      if (err.name !== 'CanceledError') {
        handleWidgetError(err);
        setLoadingFalse();
      }
    }
  };

  const handleRemoveClick = useCallback(
    (worker: DormantWorkerDTO) => {
      setWorkerToRemove(worker);
      openConfirmDialog();
    },
    [openConfirmDialog]
  );

  const handleCloseConfirmDialog = () => {
    closeConfirmDialog();
    setWorkerToRemove(null);
  };

  const handleConfirmRemove = async () => {
    if (!selectedCompanyId || !workerToRemove) {
      return;
    }
    try {
      setRemovingTrue();
      await removeDormantWorker(selectedCompanyId, workerToRemove.user_id);
      handleCloseConfirmDialog();
      await getDormantWorkers(0);
    } catch (e) {
      handleServerError(e);
    } finally {
      setRemovingFalse();
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    getDormantWorkers(0, signal);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, sortModel, substring, selectedLocationOptions, unselectedLocationOptions]);

  return {
    workers: data,
    total,
    loading,
    isMobile,
    sortModel,
    isLoadingList,
    isConfirmDialogOpen,
    isRemoving,
    workerToRemove,
    resetLoadingList,
    getDormantWorkers,
    handleSortModelChange,
    handleSearchChange,
    handleRemoveClick,
    handleCloseConfirmDialog,
    handleConfirmRemove,
  };
};
